import { useState } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, CheckCircle2, Lock, FileCheck2 } from "lucide-react";
import { Link } from "react-router-dom";

const DEVICE_TYPES = ["Smartphone", "Tablet", "Laptop", "Smartwatch", "Desktop", "Other"];

const CONSENT_TEXT =
  "By checking this box and submitting this form, I agree to receive calls and SMS/text messages from DeviceLox regarding my device registration, using the phone number provided, including messages sent via automated technology. Consent is not a condition of any purchase. Message and data rates may apply. Reply STOP to opt out. I have read and agree to the Terms, Privacy Policy, and SMS Terms.";

const empty = {
  first_name: "",
  last_name: "",
  zip: "",
  phone: "",
  email: "",
  device_type: "",
  brand: "",
  model: "",
};

export default function Home() {
  const [form, setForm] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.device_type) {
      toast.error("Please select a device type.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the consent notice to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/registrations", {
        ...form,
        tcpa_consent: consent,
        source_url: window.location.href,
      });
      setDone(true);
      toast.success("Device registered successfully.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1513348313239-6ccc6c25c11b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzbWFydCUyMGRldmljZXMlMjBjbGVhbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg3NTA3NTg0fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="relative mx-auto max-w-6xl px-5 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">
              <ShieldCheck className="h-3.5 w-3.5" /> Your consent, clearly recorded
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Register your device.<br />Keep the record straight.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
              One simple form to submit your device and contact details. Your
              consent is captured clearly and stored securely — no accounts, no
              clutter.
            </p>
            <a href="#register" data-testid="hero-register-cta" className="mt-8 inline-block">
              <Button size="lg" className="rounded-md bg-blue-600 px-8 text-white hover:bg-blue-500">
                Register Your Device
              </Button>
            </a>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-300">
              <span className="inline-flex items-center gap-2"><Lock className="h-4 w-4 text-blue-300" /> Private records</span>
              <span className="inline-flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-blue-300" /> Consent proof stored</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-300" /> Duplicate protected</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Registration form */}
      <section id="register" className="mx-auto max-w-3xl px-5 py-16 md:py-24">
        {done ? (
          <div className="rounded-md border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="registration-success">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-slate-900">
              You're registered
            </h2>
            <p className="mt-3 text-slate-600">
              Your device and contact details have been saved along with your
              consent record. We'll reach out if anything is needed.
            </p>
            <Button
              variant="outline"
              className="mt-6 rounded-md"
              data-testid="register-another-button"
              onClick={() => {
                setForm(empty);
                setConsent(false);
                setDone(false);
              }}
            >
              Register another device
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                Device Registration
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Tell us about your device
              </h2>
              <p className="mt-3 text-slate-600">
                All fields are required. We check for duplicates by phone and email.
              </p>
            </div>

            <form onSubmit={submit} className="rounded-md border border-slate-200 bg-white p-6 shadow-sm md:p-8" data-testid="registration-form">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Contact details</p>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                <Field label="First name" id="first_name">
                  <Input data-testid="input-first-name" required value={form.first_name} onChange={set("first_name")} placeholder="Jane" />
                </Field>
                <Field label="Last name" id="last_name">
                  <Input data-testid="input-last-name" required value={form.last_name} onChange={set("last_name")} placeholder="Doe" />
                </Field>
                <Field label="ZIP code" id="zip">
                  <Input data-testid="input-zip" required value={form.zip} onChange={set("zip")} placeholder="90210" />
                </Field>
                <Field label="Phone" id="phone">
                  <Input data-testid="input-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Email" id="email">
                    <Input data-testid="input-email" required type="email" value={form.email} onChange={set("email")} placeholder="jane@example.com" />
                  </Field>
                </div>
              </div>

              <div className="my-8 h-px bg-slate-100" />
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Device details</p>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                <Field label="Device type" id="device_type">
                  <Select value={form.device_type} onValueChange={set("device_type")}>
                    <SelectTrigger data-testid="select-device-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEVICE_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`device-type-${t.toLowerCase()}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Brand" id="brand">
                  <Input data-testid="input-brand" required value={form.brand} onChange={set("brand")} placeholder="Apple" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Model" id="model">
                    <Input data-testid="input-model" required value={form.model} onChange={set("model")} placeholder="iPhone 15 Pro" />
                  </Field>
                </div>
              </div>

              {/* TCPA consent */}
              <div className="mt-8 rounded-md border-2 border-blue-100 bg-blue-50/50 p-5">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="tcpa"
                    data-testid="tcpa-consent-checkbox"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(!!v)}
                    className="mt-1 h-5 w-5"
                  />
                  <Label htmlFor="tcpa" className="cursor-pointer text-sm leading-relaxed text-slate-700">
                    {CONSENT_TEXT.replace(" I have read and agree to the Terms, Privacy Policy, and SMS Terms.", " ")}
                    I have read and agree to the{" "}
                    <Link to="/terms" className="font-semibold text-blue-600 underline">Terms</Link>,{" "}
                    <Link to="/privacy" className="font-semibold text-blue-600 underline">Privacy Policy</Link>, and{" "}
                    <Link to="/sms-terms" className="font-semibold text-blue-600 underline">SMS Terms</Link>.
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="register-submit-button"
                disabled={submitting}
                className="mt-6 w-full rounded-md bg-blue-600 py-6 text-base text-white hover:bg-blue-500 sm:w-auto sm:px-10"
              >
                {submitting ? "Registering…" : "Register Device"}
              </Button>
            </form>
          </>
        )}
      </section>
    </PublicLayout>
  );
}

const Field = ({ label, id, children }) => (
  <div>
    <Label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-700">
      {label}
    </Label>
    {children}
  </div>
);
